import { useEffect, useState } from "react";

import App from "./App";
import {
  mergeMemberIdentity,
  redeemInvite,
  friendlyError,
} from "./data/callables";
import {
  clearTeamId,
  loadTeamId,
  parseJoinUrl,
  saveTeamId,
} from "./data/teamSession";
import { useAuth } from "./hooks/useAuth";
import { useMembership, useUserDirectory } from "./hooks/useMembership";
import { normalizeApplicationError } from "./shared/errors";
import {
  ClaimGate,
  JoinAuthChoice,
  JoinError,
  Landing,
  Status,
} from "./components/onboarding/Onboarding";
import { Migrate, migrationAvailable } from "./components/onboarding/Migrate";

type RedeemState = "pending" | "done" | "error";

export default function AppRoot() {
  const [join] = useState(() => parseJoinUrl(window.location));
  const [storedTeamId, setStoredTeamId] = useState(() => loadTeamId());
  const [showMigrate, setShowMigrate] = useState(false);
  const [migrationAuthError, setMigrationAuthError] = useState(false);
  const {
    user,
    authReady,
    authError,
    signInGoogle,
    signInGoogleEmu,
    signInAnonymous,
    linkGoogle,
  } = useAuth();
  const { directory, error: directoryError } = useUserDirectory(user?.uid);
  const directoryTeamIds = Array.isArray(directory?.teamIds)
    ? directory.teamIds.filter((id) => typeof id === "string" && id)
    : [];
  const directoryTeamId =
    typeof directory?.defaultTeamId === "string" && directory.defaultTeamId
      ? directory.defaultTeamId
      : (directoryTeamIds[0] ?? null);
  const teamId = join?.teamId ?? storedTeamId ?? directoryTeamId;
  const [redeemState, setRedeemState] = useState<RedeemState>(
    join ? "pending" : "done",
  );
  const [redeemError, setRedeemError] = useState<Error | null>(null);

  useEffect(() => {
    if (join || storedTeamId || !directoryTeamId) return;
    saveTeamId(directoryTeamId);
    setStoredTeamId(directoryTeamId);
  }, [join, storedTeamId, directoryTeamId]);

  // Redeem the invite once signed in. Redeeming is naturally idempotent server-side,
  // so a member following the link again just lands back in the app.
  useEffect(() => {
    if (!join || !user || redeemState !== "pending") return;
    if (!join.token) {
      setRedeemError(
        new Error(
          "This invite link is missing its token. Ask a teammate for a fresh link.",
        ),
      );
      setRedeemState("error");
      return;
    }
    let cancelled = false;
    redeemInvite({ teamId: join.teamId, token: join.token })
      .then(() => {
        if (cancelled) return;
        saveTeamId(join.teamId);
        setStoredTeamId(join.teamId);
        window.history.replaceState(null, "", "/"); // strip token fragment from the address bar
        setRedeemState("done");
      })
      .catch((e) => {
        if (cancelled) return;
        setRedeemError(normalizeApplicationError(e, friendlyError(e)));
        setRedeemState("error");
      });
    return () => {
      cancelled = true;
    };
  }, [join, user, redeemState]);

  const { member, error: memberError } = useMembership(
    redeemState === "done" ? teamId : null,
    user?.uid,
  );

  const directoryHasTeam = teamId ? directoryTeamIds.includes(teamId) : false;
  useEffect(() => {
    if (
      !teamId ||
      !user ||
      user.isAnonymous ||
      !member ||
      directory === undefined ||
      directoryHasTeam
    ) {
      return;
    }
    mergeMemberIdentity({ teamId }).catch((error) => {
      console.error("Couldn't update the user team directory.", error);
    });
  }, [teamId, user, member, directory, directoryHasTeam]);

  // Drop the join attempt and reload — a stored team session (if any) takes over.
  const dismissJoin = () => {
    window.history.replaceState(null, "", "/");
    window.location.reload();
  };

  // Membership is gone — forget the stored team entirely.
  const resetMembership = () => {
    clearTeamId();
    window.history.replaceState(null, "", "/");
    window.location.reload();
  };

  const beginMigration = async () => {
    try {
      if (!user) await signInAnonymous();
      setShowMigrate(true);
    } catch {
      setMigrationAuthError(true);
    }
  };

  if (authError)
    return (
      <JoinError message="Couldn't sign you in. Check your connection and reload." />
    );
  if (migrationAuthError) {
    return (
      <JoinError
        message="Couldn't start the import. Check your connection and try again."
        onReset={() => setMigrationAuthError(false)}
      />
    );
  }
  if (join && !join.token) {
    return (
      <JoinError
        message="This invite link is missing its token. Ask a teammate for a fresh link."
        onReset={dismissJoin}
      />
    );
  }
  if (showMigrate) {
    return <Migrate onCancel={() => setShowMigrate(false)} />;
  }
  if (join && authReady && !user) {
    return (
      <JoinAuthChoice
        onSignInGoogle={signInGoogleEmu ? undefined : signInGoogle}
        onDevSignIn={signInGoogleEmu}
        onContinueAnonymous={signInAnonymous}
      />
    );
  }
  if (!teamId) {
    if (!authReady) {
      return <Status>Finding your team...</Status>;
    }
    if (directoryError) {
      return (
        <JoinError message="Couldn't find your team. Check your connection and reload." />
      );
    }
    if (user && directory === undefined) {
      return <Status>Finding your team...</Status>;
    }
    const googleEmail =
      user?.providerData?.find((p) => p?.providerId === "google.com")?.email ??
      null;
    return (
      <Landing
        onImport={migrationAvailable() ? beginMigration : undefined}
        onSignInGoogle={signInGoogleEmu ? undefined : signInGoogle}
        onDevSignIn={signInGoogleEmu}
        signedInEmail={googleEmail}
      />
    );
  }
  if (!authReady || !user) {
    return (
      <Landing
        onSignInGoogle={signInGoogleEmu ? undefined : signInGoogle}
        onDevSignIn={signInGoogleEmu}
        onImport={migrationAvailable() ? beginMigration : undefined}
      />
    );
  }
  if (redeemState === "error") {
    return <JoinError message={redeemError?.message} onReset={dismissJoin} />;
  }
  if (redeemState !== "done") return <Status>Joining your team…</Status>;
  if (memberError)
    return (
      <JoinError message="Couldn't load your team. Check your connection and reload." />
    );
  if (member === undefined) return <Status>Loading your team…</Status>;
  if (member === null) {
    return (
      <JoinError
        message="You're not a member of this team (anymore). You'll need a fresh invite link."
        onReset={resetMembership}
      />
    );
  }
  if (!member.claimedPlayerId) {
    return <ClaimGate teamId={teamId} linkGoogle={linkGoogle} />;
  }

  return (
    <App teamId={teamId} user={user} member={member} linkGoogle={linkGoogle} />
  );
}
